//Helpers
import { get } from 'svelte/store';
import { devices_config } from '@devices/index';
//Stores
import { discovered_info } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { device_object } from 'src/stores/wizards';
import { addDeviceModal } from 'src/stores/device-runtime/firmware-ui';
import { get_mode, isDistance, moduleTransformId } from '../interface_helpers';
import { getTemplateDeviceInfo, isXT1Module } from '../xt1_helpers/template_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { WebBLEData } from './web_bluetooth_types';
//Constants
import { DISTANCE_ADDITIONAL_PROPS } from '../distance_helper';
import { SLEEPING_TYPES, WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR } from 'src/constants';

export const preparingDeviceForInclusion = (
	deviceInfo: DeviceInfoG2,
	status: DeviceStatusG2,
	settings: DeviceSettingsG2,
) => {
	const type = deviceInfo.model;
	const mode = get_mode({ ...deviceInfo, type }, status, settings); //get mode will return the proper mode of the device because profile for example will return 'switch' which is invalid mode for us. This will fix https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2057
	let id = deviceInfo.id?.split('-')?.pop();
	const jti = deviceInfo.jti || '';
	const device: Device = {
		id: jti ? moduleTransformId(id, jti) : id.toLowerCase(),
		type,
		category: mode === 'roller' || mode === 'cover' ? 'roller' : devices_config[type]?.family,
		position: -1,
		gen: (deviceInfo?.gen || 2).toString(),
		channel: 0,
		channels_count: devices_config[type]?.modes[mode]?.channels || 1,
		mode,
		name: undefined,
		room_id: undefined,
		image: `images/device_images/${type}.png`,
		cloud_options: { exclude_event_log: false },
		bundle: typeof status?.ble?.dev_list !== 'undefined',
		ip: status.eth?.ip ? status.eth.ip : status.wifi?.sta_ip,
		jti,
		ss: SLEEPING_TYPES.includes(type) ? { status, settings } : undefined,
	};

	if (isXT1Module(type)) {
		const config = getTemplateDeviceInfo(deviceInfo);
		device.service_type = config.type;
		device.image = config.image;
		device.category = config.category;
	}

	discovered_info.set({ [device.id]: deviceInfo });

	const tempStatus = status?.['temperature:0'];
	if (
		WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR.includes(type) &&
		((!tempStatus?.tC && !tempStatus?.tF) || (tempStatus?.tC < 0 && tempStatus?.tF < 0))
	) {
		device.hideCharts = true;
	}

	console.log('%c Will pass device data to association Modal: ', 'color:green', device);

	// Skipping enableCloudAfterAccountAssociation due to CORS blocking HTTP requests in the browser.
	// Cloud option is enabled via Bluetooth RPC instead.
	device_object.reset();
	device_object.set(device);
	// use this if you want to open TemplatesModal from inclusion process
	//saveSettingsToDevice(device, settings);
	addDeviceModal.set(true);
};

/** First known device type token from a Web BT scan filter (e.g. `SBBT-102CEU|BLU Wall Switch 2 ZB`). */
export const getPreferredBluTypeFromScanFilter = (scanFilter?: string): string | undefined =>
	scanFilter?.split('|').find((part) => Boolean(devices_config[part]));

export const preparingBLUDeviceForInclusion = (deviceInfo: WebBLEData, bleName?: string) => {
	let type = deviceInfo.type == 'SBDW-002D' ? 'SBDW-002C' : deviceInfo.type;

	if (/SBBT-102CEU|BLU Wall Switch 2 ZB/i.test(bleName || '')) {
		type = 'SBBT-102CEU';
	} else if (/SBRK-002C/i.test(bleName || '') || type === 'ShellyBLURotaryKnob') {
		type = 'SBRK-002C';
	} else if (devices_config[type] === undefined) {
		const matchedEntry = Object.entries(devices_config).find(
			([, config]) => config.name?.toLowerCase() === type?.toLowerCase(),
		);
		if (matchedEntry) {
			type = matchedEntry[0];
		}
	}

	if (devices_config[type] === undefined) {
		console.error('Trying to add unknown BLU device type:', type);
		return;
	}

	const mode = devices_config[type]?.family || getBLUDeviceMode(type);
	let device: Device = {
		id: deviceInfo.id,
		type,
		category: mode,
		position: -1,
		gen: 'GBLE',
		channel: 0,
		channels_count: devices_config[type]?.modes?.[mode]?.channels || 1,
		mode,
		name: undefined,
		room_id: undefined,
		image: `images/device_images/${type}.png`,
		cloud_options: { exclude_event_log: false },
		key_enc: deviceInfo?.encryption_key || '',
		beacon_enabled: deviceInfo?.beacon_mode,
		buzzer_enabled: deviceInfo?.buzzer,
	};

	if (isDistance(type)) device = { ...device, ...DISTANCE_ADDITIONAL_PROPS };

	console.log('%c Will pass BLU device data to association Modal: ', 'color:green', device);

	device_object.reset();
	device_object.set(device);
	addDeviceModal.set(true);
};

export const checkDeviceAccount = (deviceInfo: DeviceInfoG2): Device => {
	let id = deviceInfo.id?.split('-')?.pop();
	const jti = deviceInfo.jti || '';
	id = jti ? moduleTransformId(id, jti) : id.toLowerCase();
	return get(devices)?.[id];
};

export const checkBLUDeviceAccount = (deviceInfo: WebBLEData): Device => {
	return get(devices)?.[deviceInfo.id];
};

export const getBLUDeviceMode = (type: string) => {
	return /^(SBDW|SBHT|SBMO|SBDS|SBSM)-\w/.test(type) ? 'sensor' : 'inputs_reader';
};
